/**
 * RegisterPage Component
 * Self-service registration for new agency primary contacts.
 * Two-step form: (1) Email + Agent ID  (2) Choose username → success screen.
 * Matches the Forgot Password / Reset Password auth page design language.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  BuildingOfficeIcon,
  UserIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { oathRegister, oathCheckUsername } from '../../services/oathAuthService';
import { SimpleSpinner } from '../ui/Spinner';

// Username validation regex — letters, numbers, dots, hyphens, underscores; must start/end with alphanumeric
const USERNAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9._-]*[a-zA-Z0-9]$/;
const USERNAME_SINGLE_CHAR = /^[a-zA-Z0-9]$/;

const validateUsername = (value) => {
  if (!value) return 'Username is required';
  if (value.length < 3) return 'Username must be at least 3 characters';
  if (value.length > 50) return 'Username must be 50 characters or less';
  if (value.length === 1 && USERNAME_SINGLE_CHAR.test(value)) return 'Username must be at least 3 characters';
  if (value.length === 2) return 'Username must be at least 3 characters';
  if (!USERNAME_REGEX.test(value)) return 'Only letters, numbers, dots, hyphens, and underscores — must start and end with a letter or number';
  return null;
};

// Shared layout pieces
const PageShell = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
    {/* Decorative background */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
    </div>

    {/* Mobile Header */}
    <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
      <div className="flex flex-col items-center gap-1">
        <img src="/bankers-surety-logo.png" alt="Bankers Surety" className="h-9" />
        <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
      </div>
    </div>

    {children}

    {/* Footer */}
    <footer className="py-3 px-4 text-center relative z-10">
      <p className="text-xs text-primary-200/80">
        &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
      </p>
    </footer>
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();

  // Steps: 'identity' → 'username' → 'success'
  const [step, setStep] = useState('identity');

  // Step 1 fields
  const [email, setEmail] = useState('');
  const [agentId, setAgentId] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Step 2 fields
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null | true | false
  const [checkingUsername, setCheckingUsername] = useState(false);
  const checkTimeoutRef = useRef(null);

  // Shared state
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Suggested username placeholder (generated from hypothetical name — user fills in)
  const suggestedUsername = 'firstname.lastname.' + (agentId || 'agentid');

  // ---- Step 1: Validate identity ----
  const handleIdentitySubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    if (!agentId.trim()) {
      setError('Please enter your Agent ID');
      return;
    }
    if (!/^\d+$/.test(agentId.trim())) {
      setError('Agent ID must be a number');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the registration terms to continue');
      return;
    }

    setStep('username');
  };

  // ---- Step 2: Username selection + debounced availability check ----
  const checkAvailability = useCallback(async (value) => {
    const validationError = validateUsername(value);
    if (validationError) {
      setUsernameError(validationError);
      setUsernameAvailable(null);
      setCheckingUsername(false);
      return;
    }

    setCheckingUsername(true);
    setUsernameError(null);
    const result = await oathCheckUsername(value.toLowerCase());
    setCheckingUsername(false);

    if (result.success) {
      setUsernameAvailable(result.available);
      if (!result.available) {
        setUsernameError('This username is already taken');
      }
    } else {
      setUsernameError(result.error || 'Could not check availability');
      setUsernameAvailable(null);
    }
  }, []);

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setUsernameAvailable(null);
    setUsernameError(null);
    setError('');

    // Clear pending check
    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);

    if (!value.trim()) return;

    // Immediate format validation
    const validationError = validateUsername(value);
    if (validationError) {
      setUsernameError(validationError);
      return;
    }

    // Debounced availability check (500ms)
    setCheckingUsername(true);
    checkTimeoutRef.current = setTimeout(() => checkAvailability(value), 500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
    };
  }, []);

  // ---- Submit registration ----
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateUsername(username);
    if (validationError) {
      setUsernameError(validationError);
      return;
    }
    if (usernameAvailable === false) {
      setUsernameError('This username is already taken');
      return;
    }

    setIsSubmitting(true);
    const result = await oathRegister(email.trim(), parseInt(agentId.trim(), 10), username.toLowerCase());
    setIsSubmitting(false);

    if (result.success) {
      setStep('success');
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  const handleBackToLogin = () => navigate('/');

  // ========== SUCCESS SCREEN ==========
  if (step === 'success') {
    return (
      <PageShell>
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1 bg-gradient-to-r from-green-400 via-green-500 to-green-400" />
              <div className="p-6 sm:p-8 lg:p-10 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Registration Successful
                </h2>
                <p className="text-sm text-gray-600 mb-2">
                  We've sent a welcome email to <span className="font-semibold">{email}</span> with a link to set your password.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Your username is <span className="font-mono font-semibold text-gray-700">{username.toLowerCase()}</span>
                </p>
                <button
                  onClick={handleBackToLogin}
                  className="w-full py-3.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </main>
      </PageShell>
    );
  }

  // ========== STEP 2: USERNAME ==========
  if (step === 'username') {
    const canSubmit = username.trim() && !usernameError && usernameAvailable === true && !checkingUsername && !isSubmitting;

    return (
      <PageShell>
        <main className="flex-1 flex items-center justify-center px-4 py-4 sm:py-6 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Heading */}
            <div className="text-center mb-5 sm:mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
                Choose Your Username
              </h1>
              <p className="text-sm text-primary-100 mt-1">
                This is how you'll sign in
              </p>
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />
              <div className="p-5 sm:p-6 lg:p-8">
                {/* Error */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                    <p className="text-sm text-red-700 text-center">{error}</p>
                  </div>
                )}

                <form onSubmit={handleRegisterSubmit} noValidate autoComplete="off">
                  {/* Username field */}
                  <div className="mb-4">
                    <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Username
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={handleUsernameChange}
                        disabled={isSubmitting}
                        autoFocus
                        autoComplete="off"
                        placeholder={suggestedUsername}
                        className="w-full pl-12 pr-10 py-3 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                      />
                      {/* Status indicator */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingUsername && <SimpleSpinner size="sm" className="text-gray-400" />}
                        {!checkingUsername && usernameAvailable === true && (
                          <CheckIcon className="h-5 w-5 text-green-500" />
                        )}
                        {!checkingUsername && usernameError && username.trim() && (
                          <XMarkIcon className="h-5 w-5 text-red-400" />
                        )}
                      </div>
                    </div>
                    {/* Feedback text */}
                    {usernameError && username.trim() && (
                      <p className="mt-1.5 text-xs text-red-600">{usernameError}</p>
                    )}
                    {!checkingUsername && usernameAvailable === true && (
                      <p className="mt-1.5 text-xs text-green-600">Username is available</p>
                    )}
                    {!username.trim() && (
                      <p className="mt-1.5 text-xs text-gray-400">
                        Suggested format: firstname.lastname.{agentId || 'agentid'}
                      </p>
                    )}
                  </div>

                  {/* Username rules */}
                  <div className="mb-5 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Username rules</p>
                    <ul className="space-y-1">
                      {[
                        { label: '3–50 characters', met: username.length >= 3 && username.length <= 50 },
                        { label: 'Letters, numbers, dots, hyphens, underscores', met: username.length >= 3 && USERNAME_REGEX.test(username) },
                        { label: 'Starts and ends with a letter or number', met: username.length >= 2 && /^[a-zA-Z0-9]/.test(username) && /[a-zA-Z0-9]$/.test(username) },
                      ].map((rule) => (
                        <li key={rule.label} className="flex items-center gap-2">
                          {username.trim() ? (
                            rule.met
                              ? <CheckIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                              : <XMarkIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                          ) : (
                            <div className="h-3.5 w-3.5 rounded-full border border-gray-300 flex-shrink-0" />
                          )}
                          <span className={`text-xs ${username.trim() && rule.met ? 'text-green-700' : 'text-gray-500'}`}>{rule.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Buttons row */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setStep('identity'); setError(''); }}
                      disabled={isSubmitting}
                      className="flex-1 py-3 px-4 text-gray-600 hover:text-primary font-medium rounded-xl border border-gray-300 hover:border-primary/30 transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="flex-[2] py-3 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      {isSubmitting ? (
                        <>
                          <SimpleSpinner size="sm" className="text-white mr-2" />
                          <span>Registering...</span>
                        </>
                      ) : (
                        <>
                          Register
                          <ArrowRightIcon className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Help */}
            <div className="mt-5 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <span className="text-sm text-primary-100">Need assistance?</span>
                <a href="tel:877-778-5245" className="text-sm text-white font-semibold hover:text-primary-50 transition-colors">
                  877-778-5245
                </a>
              </div>
            </div>
          </div>
        </main>
      </PageShell>
    );
  }

  // ========== STEP 1: IDENTITY ==========
  return (
    <PageShell>
      <main className="flex-1 flex items-center justify-center px-4 py-4 sm:py-6 relative z-10">
        <div className="w-full max-w-sm sm:max-w-md">
          {/* Heading */}
          <div className="text-center mb-5 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
              Register Your Agency
            </h1>
            <p className="text-sm text-primary-100 mt-1">
              Set up portal access for your agency
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
            <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />
            <div className="p-5 sm:p-6 lg:p-8">
              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
              )}

              <p className="text-sm text-gray-600 text-center mb-4">
                You must be a registered agent with Bankers Surety and the primary contact for your agency.
              </p>

              <form onSubmit={handleIdentitySubmit} noValidate>
                {/* Email */}
                <div className="mb-4">
                  <label htmlFor="reg-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="reg-email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      autoComplete="email"
                      autoFocus
                      placeholder="Email registered with Bankers Surety"
                      className="w-full pl-12 pr-4 py-3 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Agent ID */}
                <div className="mb-4">
                  <label htmlFor="reg-agentid" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Agent ID
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="reg-agentid"
                      type="text"
                      inputMode="numeric"
                      value={agentId}
                      onChange={(e) => { setAgentId(e.target.value.replace(/\D/g, '')); setError(''); }}
                      autoComplete="off"
                      placeholder="Your agency's Agent ID number"
                      className="w-full pl-12 pr-4 py-3 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Terms checkbox */}
                <div className="mb-5">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => { setAcceptedTerms(e.target.checked); setError(''); }}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">
                      I confirm that I am the primary contact for this agency and am authorized to create a portal account.
                    </span>
                  </label>
                </div>

                {/* Buttons row */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="flex-1 py-3 px-4 text-gray-600 hover:text-primary font-medium rounded-xl border border-gray-300 hover:border-primary/30 transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Sign In
                  </button>
                  <button
                    type="submit"
                    className="flex-[2] py-3 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Continue
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Help */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-sm text-primary-100">Need assistance?</span>
              <a href="tel:877-778-5245" className="text-sm text-white font-semibold hover:text-primary-50 transition-colors">
                877-778-5245
              </a>
            </div>
          </div>
        </div>
      </main>
    </PageShell>
  );
};

export default RegisterPage;
